import { useEffect, useState } from "react";
import {
    TextField,
    Button,
    Box,
    MenuItem,
    Grid,
    Stack,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    CircularProgress,
} from "@mui/material";
import { User } from "../../../types/userTypes";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import SuperAdminUserService from './../../../service/superadmin/userService';
import { Company } from "../../../types/companyTypes";
import SuperAdminCompanyService from "../../../service/superadmin/companyService";
interface EditUserProps {
    open: boolean;
    onClose: () => void;
    user: User | null;
    refreshUsers: () => void;
}

const EditUser: React.FC<EditUserProps> = ({ open, onClose, user, refreshUsers }) => {
    const [loading, setLoading] = useState<boolean>(false);
    const [companyList, setCompanyList] = useState<Company[]>([]);

    const fetchCompanies = async () => {
        try {
            const companies = await SuperAdminCompanyService.getCompanyList();
            setCompanyList(companies);
        } catch (error) {
            toast.error("Failed to load company list.");
        }
    };

    useEffect(() => {
        fetchCompanies();
    }, []);

    const validationSchema = Yup.object({
        name: Yup.string().required("Name is required"),
        username: Yup.string().required("Username is required"),
        email: Yup.string()
            .matches(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, "Invalid email format")
            .required("Email is required"),
        phone: Yup.string()
            .matches(/^\d+$/, "Phone number must be numeric")
            .min(8, "Phone number must be at least 8 digits")
            .required("Phone is required"),
        address: Yup.string().required("Address is required"),
        country_code: Yup.string().required("Country Code is required"),
        role: Yup.string().required("Role is required"),
        status: Yup.string().required("Status is required"),
    });


    const formik = useFormik({

        initialValues: {
            name: user?.name || "",
            username: user?.username || "",
            email: user?.email || "",
            phone: user?.phone ? user?.phone.replace(user?.country_code, "").trim() : "",
            country_code: user?.country_code || "+1",
            address: user?.address || "",
            // role: user?.role || "user",
            role:
                user?.role === "user" && user?.is_company_admin === "1"
                    ? "companyadmin"
                    : user?.role || "user",
            status: user?.status || "1",
            is_company_admin: user?.is_company_admin?.toString() || "2",
            company_id: user?.company_id?.toString() || "",
        },
        enableReinitialize: true,
        validationSchema,
        onSubmit: async (values) => {
            setLoading(true);
            try {
                const updatedValues = {
                    ...values,
                    phone: values.phone.toString(),
                    role: values.role === "companyadmin" ? "user" : values.role,
                    is_company_admin: values.role === "companyadmin" ? "1" : "2",
                    company_id: values.company_id,
                };
                await SuperAdminUserService.updateUser(user?.id as number, updatedValues);
                toast.success("User updated successfully!");
                refreshUsers();
                onClose();
            } catch (error) {
                toast.error("Failed to update user.");
            } finally {
                setLoading(false);
            }
        },
    });

    const isAdmin = formik.values.role === "superadmin" || formik.values.role === 'admin';

    const roleOptions = isAdmin
        ? [
            { value: "admin", label: "Admin" },
            { value: "superadmin", label: "Super Admin" },
        ]
        : [
            { value: "user", label: "User" },
            { value: "companyadmin", label: "Company Admin" },
            // { value: "admin", label: "Admin" },
            // { value: "superadmin", label: "Super Admin" },
        ];

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>Edit User</DialogTitle>
            <DialogContent>
                <Box component="form" onSubmit={formik.handleSubmit} sx={{ width: "100%", p: 2 }}>
                    <Grid container spacing={2}>
                        {/* Name & Username */}
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                label="Name"
                                name="name"
                                value={formik.values.name}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={formik.touched.name && Boolean(formik.errors.name)}
                                helperText={formik.touched.name && formik.errors.name}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                label="Username"
                                name="username"
                                value={formik.values.username}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={formik.touched.username && Boolean(formik.errors.username)}
                                helperText={formik.touched.username && formik.errors.username}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Email"
                                name="email"
                                value={formik.values.email}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={formik.touched.email && Boolean(formik.errors.email)}
                                helperText={formik.touched.email && formik.errors.email}
                            />
                        </Grid>

                        {/* Country Code & Phone */}
                        <Grid item xs={4}>
                            <TextField
                                select
                                fullWidth
                                label="Country Code"
                                name="country_code"
                                value={formik.values.country_code}
                                onChange={formik.handleChange}
                            >
                                <MenuItem value="+1">+1 (US)</MenuItem>
                                <MenuItem value="+44">+44 (UK)</MenuItem>
                                <MenuItem value="+33">+33 (France)</MenuItem>
                                <MenuItem value="+49">+49 (Germany)</MenuItem>
                                <MenuItem value="+65">+65 (Singapore)</MenuItem>
                                <MenuItem value="+60">+60 (Malaysia)</MenuItem>
                            </TextField>
                        </Grid>
                        <Grid item xs={8}>
                            <TextField
                                fullWidth
                                type="number"
                                label="Phone"
                                name="phone"
                                value={formik.values.phone}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={formik.touched.phone && Boolean(formik.errors.phone)}
                                helperText={formik.touched.phone && formik.errors.phone}
                            />
                        </Grid>

                        {/* Address */}
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Address"
                                name="address"
                                value={formik.values.address}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={formik.touched.address && Boolean(formik.errors.address)}
                                helperText={formik.touched.address && formik.errors.address}
                            />
                        </Grid>
                        <Grid item xs={8}>
                            <TextField
                                select
                                fullWidth
                                label="Role"
                                name="role"
                                value={formik.values.role}
                                onChange={formik.handleChange}
                            >
                                {roleOptions.map((option) => (
                                    <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>

                        <Grid item xs={4}>
                            <TextField
                                select
                                fullWidth
                                label="Status"
                                name="status"
                                value={formik.values.status}
                                onChange={formik.handleChange}
                            >
                                <MenuItem value="1">Active</MenuItem>
                                <MenuItem value="2">Inactive</MenuItem>
                            </TextField>
                        </Grid>

                        {!isAdmin && (
                            <Grid item xs={12}>
                                <TextField
                                    select
                                    fullWidth
                                    label="Company"
                                    name="company_id"
                                    value={
                                        companyList.find((c) => c.id.toString() === user?.company_id)?.id || "N/A"
                                    }
                                    onChange={formik.handleChange}
                                    disabled={true}
                                >
                                    {companyList.map((company) => (
                                        <MenuItem key={company.id} value={company.id.toString()}>
                                            {company.company_name}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                        )}


                    </Grid>

                    <DialogActions sx={{ mt: 2 }}>
                        <Stack direction="row" spacing={2}>
                            <Button variant="contained" color="primary" type="submit" disabled={loading}>
                                {loading ? <CircularProgress size={24} /> : "Save Changes"}
                            </Button>
                            <Button variant="outlined" color="secondary" onClick={onClose} disabled={loading}>
                                Cancel
                            </Button>
                        </Stack>
                    </DialogActions>
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default EditUser;
